import { AssetRequestError } from '../api/getAssetById';
import { isIpMismatchAuthError } from '../api/httpErrorFormat';

export type BookingProfileAuthBlockReason = 'ipMismatch' | 'session';

function authResponseText(error: AssetRequestError): string {
  return `${error.rawBody ?? ''}\n${error.errorDescription ?? ''}\n${error.message ?? ''}`;
}

/** True when GET /profile failed because credentials were rejected (401/403). */
export function isBookingProfileFetchAuthError(err: unknown): err is AssetRequestError {
  return err instanceof AssetRequestError && (err.httpStatus === 401 || err.httpStatus === 403);
}

/** True when POST /bookings/create rejected a lite / incomplete registration. */
export function isIncompleteRegistrationApiMessage(message: string | null | undefined): boolean {
  const normalized = message?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes('complete registration before booking') ||
    normalized.includes('complete your registration before booking') ||
    normalized.includes('finish registration before booking') ||
    (normalized.includes('complete registration') && normalized.includes('book'))
  );
}

/** True when a booking API error is the incomplete-registration rejection. */
export function isIncompleteRegistrationBookingError(err: unknown): boolean {
  if (typeof err === 'string') {
    return isIncompleteRegistrationApiMessage(err);
  }
  if (err instanceof AssetRequestError) {
    return isIncompleteRegistrationApiMessage(authResponseText(err));
  }
  if (err instanceof Error) {
    return isIncompleteRegistrationApiMessage(err.message);
  }
  return false;
}

/** Classify auth failures for booking profile gate UX. */
export function resolveBookingProfileAuthBlockReason(
  err: unknown,
): BookingProfileAuthBlockReason | null {
  if (!isBookingProfileFetchAuthError(err)) {
    return null;
  }
  if (isIpMismatchAuthError(authResponseText(err))) {
    return 'ipMismatch';
  }
  return 'session';
}
